import {AxiosError, AxiosResponse, HttpStatusCode} from 'axios';
import {t} from 'i18next';
import {useCallback, useEffect, useRef, useState} from 'react';
import {getFCEncryptedPayload} from '../../api/EvidenceSourceUpload';
import {
  getAgentHeartbeat,
  getConnectionKeyPostRequest,
} from '../../api/FCUploadFileServices';
import {showToastType} from '../../contexts/toastContext/ToastContext';
import {
  FC_AGENT_URL_SCHEME,
  FC_CONNECTION_API_CYCLE_DELAY,
  FC_CONNECTION_API_MAX_CYCLE_COUNT,
  FC_HEART_BEAT_CYCLE_DELAY,
  FC_HEART_BEAT_MAX_CYCLE_COUNT,
  showFCToastFromAxiosError,
} from '../../utils/fcUploadFileUtils';
import {isEmptyInstance} from '../../utils/utils';

const useFCAgentConnection = (
  enableHeartBeat: boolean,
  showToast?: showToastType
) => {
  const [connectionKey, setConnectionKey] = useState<string>('');
  const [isAgentRunning, setIsAgentRunning] = useState<boolean>(false);
  const [isConnectionFailed, setIsConnectionFailed] = useState<boolean>(false);
  const [fcEncryptedPayload, setFcEncryptedPayload] = useState<string>('');
  const [apiError, setApiError] = useState<AxiosError>();
  const heartBeatAbortController = useRef<AbortController>();
  const connectionAbortController = useRef<AbortController>();
  const fcEncryptedPayloadRef = useRef<string>('');

  const isFCHeartBeatFailed = useRef<boolean>(false);
  const isAllAPICallsCompleted = useRef<boolean>(false);

  type FCAPICallBack = (success: boolean, response?: AxiosResponse) => void;

  const setMethodCallWithCallBack = useCallback(
    async (
      methodCall: () => Promise<AxiosResponse>,
      callback?: FCAPICallBack
    ) => {
      try {
        const data = await methodCall?.();
        callback?.(data.status === HttpStatusCode.Ok, data);
        if (data.status !== HttpStatusCode.Ok) {
          isAllAPICallsCompleted.current = true;
        }
      } catch (e) {
        console.error(e);
        callback?.(false);
      }
    },
    []
  );

  const launchFCAgent = useCallback(() => {
    return new Promise((resolve) => {
      try {
        globalThis.window.location.href = FC_AGENT_URL_SCHEME;
        resolve(true);
      } catch (e) {
        console.error('Launch failed', e);
        resolve(false);
      }
    });
  }, []);

  const getFCAgentHeartBeat = useCallback(
    async (callback?: (success: boolean) => void) => {
      setMethodCallWithCallBack(() => {
        heartBeatAbortController.current = new AbortController();
        return getAgentHeartbeat(
          {
            shouldRetry: true,
            cycleDelay: FC_HEART_BEAT_CYCLE_DELAY,
            maxCycles: FC_HEART_BEAT_MAX_CYCLE_COUNT,
            onRetry: () => {
              isFCHeartBeatFailed.current = true;
              setIsAgentRunning(false);
            },
            onRetryCompleted: (error) => {
              isAllAPICallsCompleted.current = true;
              setApiError(error);
              setIsConnectionFailed(true);
            },
          },
          heartBeatAbortController.current?.signal
        );
      }, callback);
    },
    [setMethodCallWithCallBack]
  );

  const getEncryptedPayload = useCallback(
    async (callback?: FCAPICallBack) => {
      setMethodCallWithCallBack(() => {
        return getFCEncryptedPayload();
      }, callback);
    },
    [setMethodCallWithCallBack]
  );

  const getFCConnectionKey = useCallback(
    async (encryptedPayload: string, callback?: FCAPICallBack) => {
      setMethodCallWithCallBack(() => {
        connectionAbortController.current = new AbortController();
        return getConnectionKeyPostRequest(
          encryptedPayload,
          {
            shouldRetry: true,
            cycleDelay: FC_CONNECTION_API_CYCLE_DELAY,
            maxCycles: FC_CONNECTION_API_MAX_CYCLE_COUNT,
            onRetry: () => {
              isFCHeartBeatFailed.current = false;
              setIsAgentRunning(false);
            },
            onRetryCompleted: (error) => {
              isAllAPICallsCompleted.current = true;
              setApiError(error);
              setIsConnectionFailed(true);
            },
          },
          connectionAbortController.current?.signal
        );
      }, callback);
    },
    [setMethodCallWithCallBack]
  );

  const getFCConnectionKeyRequest = useCallback(
    () =>
      !isEmptyInstance(fcEncryptedPayloadRef.current) &&
      getFCConnectionKey(
        fcEncryptedPayloadRef.current,
        (success: boolean, response?: AxiosResponse) => {
          if (success) {
            setIsAgentRunning(true);
            const {connectionKey: iconnectionKey} = response?.data as {
              connectionKey: string;
            };
            setConnectionKey(iconnectionKey);
          } else {
            setIsConnectionFailed(true);
          }
        }
      ),
    [getFCConnectionKey]
  );

  const getHeartBeat = useCallback(async () => {
    setIsConnectionFailed(false);
    setApiError(undefined);
    isAllAPICallsCompleted.current = false;
    isFCHeartBeatFailed.current = false;
    getFCAgentHeartBeat((successHeartBeat: boolean) => {
      if (successHeartBeat) {
        if (isEmptyInstance(fcEncryptedPayloadRef.current)) {
          getEncryptedPayload(
            (successEncryptedPayload: boolean, response?: AxiosResponse) => {
              if (successEncryptedPayload) {
                const encryptedPayload: string = response?.data ?? '';
                fcEncryptedPayloadRef.current = encryptedPayload;
                setFcEncryptedPayload(encryptedPayload);
                getFCConnectionKeyRequest();
              }
            }
          );
        } else {
          getFCConnectionKeyRequest();
        }
      }
    });
  }, [getEncryptedPayload, getFCAgentHeartBeat, getFCConnectionKeyRequest]);

  const regenerateConnectionKey = useCallback(
    (ifcEncryptedPayload: string): Promise<string | null> => {
      return new Promise((resolve) => {
        getConnectionKeyPostRequest(ifcEncryptedPayload)
          .then((response: AxiosResponse) => {
            if (response.status === HttpStatusCode.Ok) {
              const {connectionKey} = response?.data as {connectionKey: string};
              setConnectionKey(connectionKey);
              resolve(connectionKey);
            } else {
              resolve(null);
            }
          })
          .catch((e: AxiosError) => {
            console.error(e);
            resolve(null);
          });
      });
    },
    []
  );

  const cancelFCHeartBeat = useCallback(() => {
    heartBeatAbortController.current?.abort();
    connectionAbortController.current?.abort();
    isAllAPICallsCompleted.current = false;
  }, []);

  useEffect(() => {
    !isAllAPICallsCompleted.current &&
      !isAgentRunning &&
      enableHeartBeat &&
      getHeartBeat();
  }, [enableHeartBeat, getHeartBeat, isAgentRunning]);

  useEffect(() => {
    enableHeartBeat &&
      !isEmptyInstance(apiError) &&
      !isEmptyInstance(showToast) &&
      showFCToastFromAxiosError(
        showToast,
        apiError,
        isFCHeartBeatFailed.current
          ? t('fileCatalyst.highSpeedUploads.fcConnectionKeyAPIFailedMessage')
          : t('common.somethingWrong')
      );

    !isEmptyInstance(apiError) && setApiError(undefined);
  }, [apiError, enableHeartBeat, showToast]);

  useEffect(() => {
    !enableHeartBeat && cancelFCHeartBeat();
  }, [cancelFCHeartBeat, enableHeartBeat]);

  return {
    fcEncryptedPayload,
    connectionKey,
    isAgentRunning,
    isConnectionFailed,
    regenerateConnectionKey,
    launchFCAgent,
    cancelFCHeartBeat,
    getHeartBeat,
  };
};

export default useFCAgentConnection;
